// @ts-check
// pwa/lib/shortcuts.js — the Hub's keyboard shortcuts, as pure logic.
//
// DOM-free so it runs under the node vitest environment (shortcuts.test.js). The
// browser wiring (listener, search focus, the "Keyboard shortcuts" dialog, the Esc
// fallback) lives in components/keyboard-shortcuts.js.
//
// Keys (matches learn.ncslsc.com, where `/` opens search):
//   Cmd+K (Mac) / Ctrl+K (Windows, Linux, ChromeOS)  search
//   /                                                 search
//   ?                                                 the Keyboard shortcuts dialog
//   g then d / s / r / h                              Dashboard, Sign up, Resources, History
//   g then a / l                                      Admin, Admin › Learn (only for people who can open them)
//   Esc                                               close the top dialog, menu or drawer
// Nothing but Esc fires while the person is typing in an input, textarea, select or
// rich-text editor (Tiptap / contenteditable).

/** Two-key sequences ("g" then a letter) must finish within this window. */
export const SEQUENCE_MS = 1500;

/** Input types that are not text entry: shortcuts still work when one of these has focus. */
const NON_TEXT_INPUTS = new Set(['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color', 'file', 'image']);

/**
 * `g` + letter destinations. `need` names the access check the browser wiring applies
 * (`adminArea` = admin or coordinator; `learn` = admin or a coordinator with a learn key).
 * @type {ReadonlyArray<{ key: string, path: string, label: string, need?: 'adminArea'|'learn' }>}
 */
export const GO_TARGETS = Object.freeze([
  { key: 'd', path: '/', label: 'Dashboard' },
  { key: 's', path: '/signup', label: 'Sign up' },
  { key: 'r', path: '/resources', label: 'Resources' },
  { key: 'h', path: '/history', label: 'History' },
  { key: 'a', path: '/admin', label: 'Admin', need: 'adminArea' },
  { key: 'l', path: '/admin/learn', label: 'Admin › Learn', need: 'learn' },
]);

/** @param {unknown} platform  navigator.userAgentData.platform or navigator.platform */
export function isMacPlatform(platform) {
  return /mac|iphone|ipad|ipod/i.test(String(platform || ''));
}

/**
 * True when a key press is going into something the person types in.
 * Accepts a DOM element or a plain stand-in ({ tagName, type, isContentEditable, closest }).
 * @param {any} t
 */
export function isTypingTarget(t) {
  if (!t || typeof t !== 'object') return false;
  if (t.isContentEditable === true) return true;
  const tag = String(t.tagName || '').toUpperCase();
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag === 'INPUT') return !NON_TEXT_INPUTS.has(String(t.type || 'text').toLowerCase());
  if (typeof t.getAttribute === 'function' && t.getAttribute('role') === 'textbox') return true;
  if (typeof t.closest === 'function' && t.closest('[contenteditable="true"], [contenteditable=""], .ProseMirror')) return true;
  return false;
}

/**
 * Cmd on a Mac, Ctrl everywhere else, and never both. Alt/Option never counts.
 * @param {{ metaKey?: boolean, ctrlKey?: boolean, altKey?: boolean }} e
 * @param {boolean} mac
 */
export function hasPrimaryModifier(e, mac) {
  if (e.altKey) return false;
  return mac ? (!!e.metaKey && !e.ctrlKey) : (!!e.ctrlKey && !e.metaKey);
}

/**
 * Work out what a keydown means.
 *
 * Returns the action to run (null for "not ours — leave the event alone") and the next
 * sequence state to keep. Actions: 'search' | 'help' | 'escape' | 'pending' | 'go:<path>'.
 * The caller calls preventDefault only for a non-null action other than 'pending'.
 *
 * @param {{ key?: string, metaKey?: boolean, ctrlKey?: boolean, altKey?: boolean, shiftKey?: boolean, isComposing?: boolean, repeat?: boolean }} e
 * @param {{ mac: boolean, typing: boolean, pending?: string|null, pendingAt?: number, now?: number }} ctx
 * @returns {{ action: string|null, pending: string|null, pendingAt: number }}
 */
export function resolveShortcut(e, ctx) {
  const none = { action: null, pending: null, pendingAt: 0 };
  const key = String(e.key || '');
  if (e.isComposing || key === 'Process' || key === 'Dead') return none;

  // Esc works everywhere, including while typing.
  if (key === 'Escape') return { action: 'escape', pending: null, pendingAt: 0 };

  // Search: Cmd+K / Ctrl+K. Not from inside a text box (the spec: nothing but Esc while typing).
  if (key.toLowerCase() === 'k' && !e.shiftKey && hasPrimaryModifier(e, ctx.mac)) {
    return ctx.typing ? none : { action: 'search', pending: null, pendingAt: 0 };
  }

  if (ctx.typing) return none;
  if (e.metaKey || e.ctrlKey || e.altKey) return none;   // leave browser / OS shortcuts alone
  if (e.repeat) return none;

  const now = ctx.now ?? 0;
  const live = ctx.pending === 'g' && now - (ctx.pendingAt ?? 0) <= SEQUENCE_MS;
  if (live) {
    const target = GO_TARGETS.find((t) => t.key === key);
    if (target) return { action: `go:${target.path}`, pending: null, pendingAt: 0 };
    // Anything else ends the sequence and is read as a fresh key below.
  }

  if (key === '/') return { action: 'search', pending: null, pendingAt: 0 };
  if (key === '?') return { action: 'help', pending: null, pendingAt: 0 };
  if (key === 'g') return { action: 'pending', pending: 'g', pendingAt: now };
  return none;
}

/**
 * Whether someone may use a `g` destination.
 * @param {{ need?: string }} target
 * @param {{ adminArea: boolean, learn: boolean }} access
 */
export function canGo(target, access) {
  if (!target.need) return true;
  if (target.need === 'adminArea') return !!access.adminArea;
  if (target.need === 'learn') return !!access.learn;
  return false;
}

/**
 * The rows the "Keyboard shortcuts" dialog lists, for this person and platform.
 * Admin-only destinations are left out for people who can't open them.
 * @param {{ mac: boolean, adminArea: boolean, learn: boolean }} o
 * `keys` is a list of alternatives; each alternative is pressed together (a chord), or one
 * after the other when `seq` is true ("g then d").
 * @returns {Array<{ group: string, rows: Array<{ keys: string[][], label: string, seq?: boolean }> }>}
 */
export function shortcutGroups({ mac, adminArea, learn }) {
  const mod = mac ? '⌘' : 'Ctrl';
  return [
    {
      group: 'Anywhere',
      rows: [
        { keys: [[mod, 'K'], ['/']], label: 'Search' },
        { keys: [['?']], label: 'Show these shortcuts' },
        { keys: [['Esc']], label: 'Close a dialog, menu or panel' },
      ],
    },
    {
      group: 'Go to',
      rows: GO_TARGETS.filter((t) => canGo(t, { adminArea, learn }))
        .map((t) => ({ keys: [['g', t.key]], label: t.label, seq: true })),
    },
  ];
}
